import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  useTheme,
  Collapse,
  Typography,
  Drawer,
} from "@mui/material";
import "./Drawer.css";
import logoTransmex from "../../assets/TransMexLogo-Final_CMYK.png";
import logoSwift from "../../assets/swift_Logo.png";
import ImagenPerfil from "../../assets/ImagenPerfilTransmex.png";
import Icons from "../../Utils/Icons";
import ModalGeneric from "../Generic/ModalGeneric";

export default function Drawers({
  user,
  onLogout,
  drawerOpen,
  setDrawerOpen,
  config,
  permissions,
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalDescription, setModalDescription] = useState("");
  const [bodyModal, setBodyModal] = useState(null);
  const [openSectionIndex, setOpenSectionIndex] = useState(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [itemsPermissions, setItemsPermissions] = useState([]);

  // Obtener idEmpresa del localStorage
  const idEmpresa = localStorage.getItem('empresa');
  
  // Determinar logo según idEmpresa (TransMex por default, Swift solo para idEmpresa = 2)
  const getLogo = () => {
    if (idEmpresa === '2') {
      //console.log('Drawer - Retornando logo Swift:', logoSwift);
      return logoSwift;
    } else {
      //console.log('Drawer - Retornando logo TransMex:', logoTransmex);
      return logoTransmex;
    }
  };

  // Usar permisos del prop si están disponibles (cambio de idioma), sino usar user
  useEffect(() => {
    if (permissions && permissions.length > 0) {
      setItemsPermissions(permissions);
    } else if (user?.user?.permisosWebUsuarios) {
      const filteredPermissions = user.user.permisosWebUsuarios.filter((permission) => permission?.estatus);
      setItemsPermissions(filteredPermissions);
    }
  }, [user, permissions, forceUpdate]);

  // Listener para cambios de idioma en i18n
  useEffect(() => {
    const handleLanguageChange = (lng) => {
      setForceUpdate(prev => prev + 1);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const handleLogout = () => {
    setIsModalOpen(true);
    setModalTitle(t("navigation.logout"));
    setModalDescription(t("navigation.logoutConfirm"));
    setBodyModal(<></>);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleAcceptModal = () => {
    setDrawerOpen(false);
    onLogout();
    navigate("/Account/Login");
    setIsModalOpen(false);
  };

  const handleItemClick = (index) => {
    setOpenSectionIndex((prevIndex) => (prevIndex === index ? null : index));
  };

  // Función para obtener la descripción traducida según el idioma actual
  const getTranslatedDescription = (permiso) => {
    const currentLang = i18n.language;
    
    if (currentLang === 'es') {
      // Usar campos con mayúsculas (como viene del API)
      return permiso.descripcion || permiso.Descripcion || permiso.idPermisoWebNavigation?.descripcion || permiso.idPermisoWebNavigation?.Descripcion;
    } else {
      // Usar campos con mayúsculas (como viene del API)
      return permiso.descripcionOtroLenguaje || permiso.DescripcionOtroLenguaje || permiso.idPermisoWebNavigation?.descripcionOtroLenguaje || permiso.idPermisoWebNavigation?.DescripcionOtroLenguaje || permiso.descripcion || permiso.Descripcion;
    }
  };

  // Función para obtener la descripción de acción traducida
  const getTranslatedActionDescription = (permiso) => {
    const currentLang = i18n.language;
    
    if (currentLang === 'es') {
      // Usar campos con mayúsculas (como viene del API)
      return permiso.descripcionAccion || permiso.DescripcionAccion || permiso.idPermisoWebNavigation?.descripcionAccion || permiso.idPermisoWebNavigation?.DescripcionAccion;
    } else {
      // Usar campos con mayúsculas (como viene del API)
      return permiso.descripcionAccionOtroLenguaje || permiso.DescripcionAccionOtroLenguaje || permiso.idPermisoWebNavigation?.descripcionAccionOtroLenguaje || permiso.idPermisoWebNavigation?.DescripcionAccionOtroLenguaje || permiso.descripcionAccion || permiso.DescripcionAccion;
    }
  };

  return (
    <>
      {isModalOpen && (
        <ModalGeneric
          open={isModalOpen}
          title={modalTitle}
          description={modalDescription}
          onClose={handleCloseModal}
          onAccept={handleAcceptModal}
          bodyComponent={bodyModal}
        />
      )}
      {!drawerOpen && (
        <div
          className="drawer-toggle-button"
          onClick={() => setDrawerOpen(true)}
          style={{
            cursor: "pointer",
            position: "absolute",
            left: "0px",
            top: "10px",
            width: "40px",
            height: "40px",
            backgroundColor: theme.palette.nav.main,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icons
            iconName="Reorder"
            fontSize="medium"
            style={{
              color: theme.palette.grey.main,
            }}
          />
        </div>
      )}
      <Drawer open={drawerOpen} variant="persistent" anchor="left">
        <Box className={`drawer-container`} role="presentation">
          <Box className="drawer-logo">
            <Grid
              container
              alignItems="center"
              sx={{ marginX: "10px", marginY: "5px" }}
            >
              <Box
                component="img"
                sx={{ height: 40, width: 200 }}
                alt={t("navigation.logoAlt")}
                src={getLogo()}
              />
              <Icons
                iconName="Reorder"
                fontSize="medium"
                style={{
                  color: theme.palette.grey.main,
                  marginLeft: "15px",
                  cursor: "pointer",
                }}
                onClick={() => setDrawerOpen(false)}
              />
            </Grid>
          </Box>
          <Box
            className="drawer-header"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              padding: 8,
            }}
          >
            <Box
              component="img"
              sx={{
                height: 100,
                width: 100,
                borderRadius: "50%",
                objectFit: "cover",
              }}
              alt={t("navigation.profileAlt")}
              src={
                user?.user.foto
                  ? `${config.url}/${config.paths.userImage}/${user.user.foto}`
                  : ImagenPerfil
              }
            />
            <h3
              style={{
                margin: "5px 0",
                color: theme.palette.grey.main,
                fontWeight: "bold",
              }}
            >
              {`${user?.name} ${user?.idEmployeNavigation.lastName ?? ""} ${
                user?.idEmployeNavigation.SecondLastName ?? ""
              }`}
            </h3>
            <h5
              style={{
                margin: "2px 0",
                color: theme.palette.grey.main,
              }}
            >
              {user?.email}
            </h5>
          </Box>
          <Box className="drawer-menu">
            <List
              sx={{
                width: "100%",
                maxWidth: 360,
                color: theme.palette.grey.main,
                marginTop: 1,
              }}
              component="nav"
              aria-labelledby="nested-list-subheader"
            >
              <ListItem component={Link} to="/">
                <ListItemIcon>
                  <Icons
                    iconName="Home"
                    fontSize="medium"
                    style={{
                      color: theme.palette.grey.main,
                      backgroundColor: "#51515180",
                      borderRadius: 3,
                      padding: "8px",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t("navigation.home")}
                  style={{ color: theme.palette.grey.main }}
                />
              </ListItem>
              {itemsPermissions.map((permiso, index) =>
                permiso.idPermisoWebNavigation.mostrarEnMenu &&
                permiso.idPermisoWebNavigation.estatus === true ? (
                  <React.Fragment key={index}>
                    <ListItem button onClick={() => handleItemClick(index)}>
                      <ListItemIcon>
                        <Icons
                          iconName={permiso.idPermisoWebNavigation?.icono}
                          fontSize="medium"
                          style={{
                            color: theme.palette.grey.main,
                            backgroundColor: "#51515180",
                            borderRadius: 3,
                            padding: "8px",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={getTranslatedDescription(permiso)}
                        style={{ color: theme.palette.grey.main }}
                      />
                      {permiso.open ? (
                        <Icons
                          iconName="ExpandLess"
                          style={{ color: theme.palette.grey.main }}
                        />
                      ) : (
                        <Icons
                          iconName="ExpandMore"
                          style={{ color: theme.palette.grey.main }}
                        />
                      )}
                    </ListItem>
                    <Collapse
                      in={openSectionIndex === index}
                      timeout="auto"
                      unmountOnExit
                    >
                      <ul
                        style={{
                          backgroundColor: theme.palette.blue.main,
                        }}
                      >
                        {permiso.idPermisoWebNavigation.inverseIdPermisoPadreNavigation.map(
                          (inversePermiso, subIndex) =>
                            inversePermiso.mostrarEnMenu &&
                            inversePermiso.estatus === true ? (
                              <li
                                key={`${index}-${subIndex}-list`}
                                style={{ margin: 5, marginLeft: "20px" }}
                              >
                                <Link
                                  to={`/${inversePermiso?.controlador}`}
                                  style={{
                                    paddingLeft: 10,
                                    height: "40px",
                                    marginTop: "-8px",
                                    marginBottom: "-8px",
                                    display: "flex",
                                    alignItems: "center",
                                    textDecoration: "none",
                                  }}
                                >
                                  <ListItemText
                                    primary={
                                      <Typography
                                        variant="body2"
                                        style={{
                                          color: theme.palette.grey.main,
                                        }}
                                      >
                                        {getTranslatedActionDescription(inversePermiso)}
                                      </Typography>
                                    }
                                  />
                                </Link>
                              </li>
                            ) : null
                        )}
                      </ul>
                    </Collapse>
                  </React.Fragment>
                ) : null
              )}
            </List>
          </Box>
          <Box
            className="drawer-logout"
            onClick={handleLogout}
            aria-label={t("navigation.logoutAriaLabel")}
            style={{ cursor: "pointer" }}
          >
            <Icons
              iconName="Logout"
              fontSize="medium"
              style={{ color: theme.palette.grey.main }}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
}
